export type ParsedVfsUserPath =
  | { kind: 'outside' }
  | { kind: 'usersRoot'; path: string }
  | {
      kind: 'home';
      ownerSegment: string;
      rootPath: string;
      relativePath: string;
    };

export type ThreadVfsPathScope = {
  rootPath: string;
  routeSegment: string;
  ownerSegment?: string;
  homeRootPath?: string;
  kind: 'user' | 'shared' | 'project';
};

export const USER_HOME_TRASH_DIRECTORY_NAME = '.trash';
export const LEGACY_USER_HOME_TRASH_DIRECTORY_NAME = 'Trash';

export function isUserHomeTrashDirectoryName(name: string): boolean {
  return (
    name === USER_HOME_TRASH_DIRECTORY_NAME ||
    name === LEGACY_USER_HOME_TRASH_DIRECTORY_NAME
  );
}

export function isUserHomeTrashRootPath(path: string): boolean {
  const parts = path.split('/').filter(Boolean);
  return (
    parts.length === 4 &&
    parts[1] === 'users' &&
    Boolean(parts[2]) &&
    isUserHomeTrashDirectoryName(parts[3] ?? '')
  );
}

/** Raw upload inbox folder at each member home root, and the leaf folder that
 * holds reference-category uploads. */
export const USER_HOME_UPLOADS_DIRECTORY_NAME = 'Uploads';

/**
 * True when a workspace path points inside a user-provided upload — the
 * member-home `Uploads` inbox (`/<slug>/users/<owner>/Uploads/…`) or a
 * reference-category `Uploads` folder (`…/References/<Category>/Uploads/…`,
 * user home or shared drive). These are inputs the member supplied, never files
 * the agent produced, so presentation surfaces use this to keep them out of
 * "files this work produced" summaries.
 */
export function isUserUploadInputVfsPath(path: string): boolean {
  const parts = path
    .split('/')
    .filter((segment) => segment.length > 0 && segment !== '.');
  // /<slug>/users/<owner>/Uploads/<file…>
  if (
    parts.length > 4 &&
    parts[1] === 'users' &&
    parts[3] === USER_HOME_UPLOADS_DIRECTORY_NAME
  ) {
    return true;
  }
  // Legacy composer uploads: /<slug>/users/<owner>/generations/user-uploads/<file…>
  if (
    parts.length > 5 &&
    parts[1] === 'users' &&
    parts[3] === 'generations' &&
    parts[4] === 'user-uploads'
  ) {
    return true;
  }
  // …/References/<Category>/Uploads/<file…>
  for (let index = 0; index + 3 < parts.length; index += 1) {
    if (
      parts[index] === 'References' &&
      parts[index + 2] === USER_HOME_UPLOADS_DIRECTORY_NAME
    ) {
      return true;
    }
  }
  return false;
}

/** Familiar, writable folders presented at the root of every member home. */
export const USER_HOME_STARTER_DIRECTORY_NAMES = [
  'Documents',
  'Chats',
  'Avatars',
  'Products',
  'Projects',
  // Default location for personal apps (`app init <name>`) and the target of
  // the sidebar's Apps links. Its children are ordinary app folders.
  'Apps',
  'Blueprints',
  'References',
  USER_HOME_TRASH_DIRECTORY_NAME,
  'skills',
  'Uploads',
] as const;

/** Synthetic home mounts owned by the platform rather than the member. */
export const USER_HOME_MANAGED_MOUNT_DIRECTORY_NAMES = [
  'Chats',
  USER_HOME_TRASH_DIRECTORY_NAME,
] as const;

export const REFERENCE_UPLOAD_CATEGORIES = [
  'product',
  'design',
  'pattern',
  'color',
] as const;

export type ReferenceUploadCategory =
  (typeof REFERENCE_UPLOAD_CATEGORIES)[number];

export const REFERENCE_CATEGORY_DIRECTORY_NAMES = {
  product: 'Products',
  design: 'Designs',
  pattern: 'Patterns',
  color: 'Colors',
} as const satisfies Record<ReferenceUploadCategory, string>;

export function referenceCategoryDirectoryName(
  category: ReferenceUploadCategory
): string {
  return REFERENCE_CATEGORY_DIRECTORY_NAMES[category];
}

export function requireOpaquePathSegment(value: string, label: string): string {
  const segment = value.trim();
  if (
    !segment ||
    segment === '.' ||
    segment === '..' ||
    segment.includes('/') ||
    /[\u0000-\u001f\u007f]/.test(segment)
  ) {
    throw new Error(`INVALID_${label.toUpperCase()}_VFS_SEGMENT`);
  }
  return segment;
}

export function sanitizeThreadVfsRouteSegment(
  value: string | null | undefined,
  fallback = 'unscoped'
): string {
  const cleaned = (value ?? '')
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return requireOpaquePathSegment(cleaned || fallback, 'thread_route');
}

export function requireCanonicalThreadVfsRouteSegment(value: string): string {
  const canonical = sanitizeThreadVfsRouteSegment(value);
  if (canonical !== value || canonical === 'unscoped') {
    throw new Error('INVALID_THREAD_ROUTE_ID');
  }
  return canonical;
}
